import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import moment from 'moment';
import HistoryLogsService from '../../../services/historyLog.service';
import { useLabels } from '../../../hooks/useLabels';
import { getEntityRoute } from '../../../constants/historyTranslations';

const PAGE_SIZE = 5;

// Deterministic per-row sentence-bar widths for the loading skeleton — mirrors
// DataTable.jsx's SKELETON_WIDTHS so bars don't reshuffle on re-render.
const SKELETON_WIDTHS = ['tw:w-4/5', 'tw:w-3/5', 'tw:w-2/3', 'tw:w-1/2', 'tw:w-3/4'];

const DOT_CLASSES = {
	insert: 'tw:bg-ok',
	update: 'tw:bg-accent',
	delete: 'tw:bg-danger',
	import: 'tw:bg-ok',
};

const OPERATION_LABEL = {
	insert: 'creó',
	update: 'modificó',
	delete: 'eliminó',
	import: 'importó',
};

// modelo -> singular entity.* terminology key, so the activity sentence
// reflects org-configured names (e.g. "credit" -> "Pasividad") instead of
// the raw backend model name.
const MODEL_TO_ENTITY_KEY = {
	account: 'entity.account',
	debt: 'entity.debt',
	credit: 'entity.credit',
	contract: 'entity.contract',
	asset: 'entity.asset',
	AssetSale: 'entity.assetSale',
	bill: 'entity.bill',
	payment: 'entity.payment',
};

/**
 * "Actividad reciente" feed from the Inicio handoff spec — colored dot +
 * activity sentence + relative time, sourced from GET /historyLogs/recent.
 * Insert/delete-type logs have no `responsable` (change-stream limitation,
 * see utils/historyLogUtil.js) — rendered as "Sistema" instead.
 *
 * Fixed height + client-side pagination, matching UpcomingDues' panel
 * exactly, so the two bottom panels line up instead of one stretching to
 * match the other's height (CSS Grid's default align-items: stretch).
 */
function ActivityFeed() {
	const [logs, setLogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(1);
	const { t } = useLabels();

	useEffect(() => {
		let isMounted = true;
		HistoryLogsService.getRecent(15)
			.then(({ data }) => {
				if (isMounted) setLogs(Array.isArray(data) ? data : []);
			})
			.catch((err) => console.error('Error fetching recent activity:', err))
			.finally(() => {
				if (isMounted) setLoading(false);
			});
		return () => {
			isMounted = false;
		};
	}, []);

	const totalPages = Math.max(1, Math.ceil(logs.length / PAGE_SIZE));
	const pagedLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

	return (
		<section className='tw:h-[420px] tw:flex tw:flex-col tw:bg-surface tw:border tw:border-border tw:rounded-panel tw:overflow-hidden'>
			<div className='tw:px-4 tw:py-3 tw:border-b tw:border-border'>
				<h3 className='tw:m-0 tw:text-[13px] tw:font-semibold tw:text-ink-0'>Actividad reciente</h3>
			</div>
			<div className='tw:flex-1 tw:overflow-y-auto tw:px-4 tw:py-2' aria-busy={loading || undefined}>
				{loading &&
					Array.from({ length: PAGE_SIZE }, (_, i) => (
						<div
							key={i}
							className={`tw:flex tw:gap-2.5 tw:py-2.5 ${i < PAGE_SIZE - 1 ? 'tw:border-b tw:border-border' : ''}`}
						>
							{/* .el-sk's own border-radius beats a tw:rounded-full utility in the
							    cascade (entity-list.css loads after Tailwind's unlayered utilities),
							    so the circle has to come from an inline style, not a tw: class. */}
							<span
								aria-hidden='true'
								className='el-sk tw:w-2 tw:h-2 tw:mt-1.5 tw:shrink-0'
								style={{ borderRadius: '50%' }}
							/>
							<div className='tw:flex-1 tw:min-w-0'>
								<span aria-hidden='true' className={`el-sk tw:block tw:h-[13px] ${SKELETON_WIDTHS[i % SKELETON_WIDTHS.length]}`} />
								<span aria-hidden='true' className='el-sk tw:block tw:h-[11px] tw:w-1/4 tw:mt-1.5' />
							</div>
						</div>
					))}
				{!loading && logs.length === 0 && (
					<p className='tw:text-[12.5px] tw:text-ink-3 tw:py-4'>Sin actividad reciente.</p>
				)}
				{!loading &&
					pagedLogs.map((log, index) => {
						// Excel import summaries carry a description in `id`, not a record id
						const entityRoute = log.operacion === 'import' ? null : getEntityRoute(log.modelo, log.id);
						const entityKey = MODEL_TO_ENTITY_KEY[log.modelo];
						const modeloLabel = entityKey ? t(entityKey) : log.modelo;
						const entityNode = entityRoute ? (
							<Link to={entityRoute} className='tw:text-ink-0 tw:font-medium tw:no-underline tw:hover:text-accent'>
								{log.entityLabel}
							</Link>
						) : (
							<span className='tw:font-medium'>{log.entityLabel}</span>
						);
						return (
							<div
								key={log._id}
								className={`tw:flex tw:gap-2.5 tw:py-2.5 ${index < pagedLogs.length - 1 ? 'tw:border-b tw:border-border' : ''}`}
							>
								<span className={`tw:w-2 tw:h-2 tw:rounded-full tw:mt-1.5 tw:shrink-0 ${DOT_CLASSES[log.operacion] || 'tw:bg-ink-4'}`} />
								<div className='tw:min-w-0'>
									<p className='tw:m-0 tw:text-[13px] tw:text-ink-0'>
										{log.responsable?.name || 'Sistema'} {OPERATION_LABEL[log.operacion] || log.operacion} {modeloLabel} {entityNode}
									</p>
									<p className='tw:m-0 tw:text-[11.5px] tw:text-ink-3'>{moment(log.createdAt).fromNow()}</p>
								</div>
							</div>
						);
					})}
			</div>
			{!loading && logs.length > PAGE_SIZE && (
				<div className='tw:flex tw:items-center tw:justify-between tw:px-4 tw:py-2 tw:border-t tw:border-border tw:text-[12px] tw:text-ink-3'>
					<span>
						{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, logs.length)} de {logs.length}
					</span>
					<div className='tw:flex tw:gap-1'>
						<button
							type='button'
							className='tw:w-7 tw:h-7 tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-ink-2 tw:hover:bg-surface-2 tw:disabled:opacity-40 tw:bg-transparent tw:border-0'
							onClick={() => setPage((p) => p - 1)}
							disabled={page === 1}
						>
							<FaChevronLeft size={12} />
						</button>
						<button
							type='button'
							className='tw:w-7 tw:h-7 tw:rounded-token tw:flex tw:items-center tw:justify-center tw:text-ink-2 tw:hover:bg-surface-2 tw:disabled:opacity-40 tw:bg-transparent tw:border-0'
							onClick={() => setPage((p) => p + 1)}
							disabled={page === totalPages}
						>
							<FaChevronRight size={12} />
						</button>
					</div>
				</div>
			)}
		</section>
	);
}

export default ActivityFeed;
